import axios from "axios";
import { useState } from "react";
import * as XLSX from "xlsx-js-style";
import { useAppSelector } from "../app/store";
import { toast } from "../lib/toast";
import { removeHash } from "../utils/hash";

interface UseDownloadXlsxReturn {
  loading: boolean;
  error: string | null;
  downloadXlsx: (
    apiUrl: string,
    params: Record<string, any>,
    downloadFileName: string,
    responsePath: string,
    dataProcessor: (item: any) => Record<string, any>
  ) => Promise<void>;
}

const useDownloadEmployeeXlsx = (method = "GET"): UseDownloadXlsxReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { access_token } = useAppSelector((state) => state.auth);

  const downloadXlsx = async (
    apiUrl: string,
    params: Record<string, any>,
    downloadFileName: string = "Data",
    responsePath: string,
    dataProcessor: (item: any) => Record<string, any>
  ) => {
    setLoading(true);
    setError(null);

    try {
      // const queryString = new URLSearchParams(
      //   Object.fromEntries(
      //     Object.entries(params).filter(([_, value]) => value !== undefined)
      //   )
      // ).toString();
      // const url = `${apiUrl}?${queryString}`;

      // const response = await fetch(url, {
      //   method,
      //   headers: {
      //     "Content-Type": "application/json",
      //     Authorization: `Bearer ${access_token}`,
      //     "ngrok-skip-browser-warning": "true",
      //   },
      // });

      const productType = localStorage.getItem("product-type");
      const productId = productType
        ? removeHash(JSON.parse(productType))
        : null;

      const dynamic = localStorage.getItem("dynamic");
      const dynamicData = dynamic ? JSON.parse(dynamic) : null;

      const defaultParams = {
        productId,
        supplierKeyword: removeHash(dynamicData?.dynamicKeyword) || null,
        supplierId:
          removeHash(dynamicData?.dynamicId?.split(" ")?.join("+")) || null,
      };

      const response = await axios.get(apiUrl, {
        params: { ...defaultParams, ...params },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${access_token}`,
          "ngrok-skip-browser-warning": "true",
        },
      });

      // const contentType = response.headers.get("Content-Type");
      // if (!contentType || !contentType.includes("application/json")) {
      //   throw new Error(
      //     "Unexpected response format: Received HTML or other content."
      //   );
      // }

      const result = response.data;

      const data = responsePath
        .split(".")
        .reduce(
          (acc, key) => (acc && acc[key] ? acc[key] : null),
          result?.data
        );

      if (!data) {
        throw new Error(
          "Invalid response path or missing data in API response."
        );
      }

      const processedData = data.map(dataProcessor);

      // Create worksheet and apply header styles
      const ws = XLSX.utils.json_to_sheet(processedData);
      const headerKeys = Object.keys(processedData[0] || {});

      // Apply styles for header row
      headerKeys.forEach((key, index) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { bold: true },
            alignment: { vertical: "center" },
            // border: {
            //   bottom: { style: "thin", color: { rgb: "000000" } },
            // },
            fill: {
              fgColor: { rgb: "F3F3F3" },
            },
          };
        }
      });

      // Freeze the header row (Row 1)
      ws["!freeze"] = { xSplit: 0, ySplit: 1 };

      // Ensure Excel recognizes the header row by setting the correct range
      ws["!ref"] = `A1:${XLSX.utils.encode_cell({
        r: processedData.length,
        c: headerKeys.length - 1,
      })}`;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "SheetJS");

      XLSX.writeFile(wb, `${downloadFileName}.xlsx`);
    } catch (err) {
      console.log(err);
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
      toast("error", "Excel download failed.");
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, downloadXlsx };
};

export default useDownloadEmployeeXlsx;
